import React from 'react';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ProxyFile from 'folder-items/ProxyFile';
import {DocumentBanner} from 'notebook/components/banners/DocumentBanner';
import {canEditTemplate} from 'notebook/entry-templates/canEditTemplate';
import SubtemplateActions from 'notebook/entry-templates/SubtemplateActions';
import SubtemplateStore from 'notebook/entry-templates/SubtemplateStore';
import {Subtemplate, TemplateCollectionSummary} from 'notebook/entry-templates/TemplateConstants';
import ReviewConstants, {Review} from 'notebook/reviews/ReviewConstants';
import {ItemEditorState} from 'notebook/store/VersionedItemSubstore';
import {NotePart} from 'notebook/util/NoteUtilConstants';

class SubtemplateFile extends ProxyFile<Subtemplate, typeof SubtemplateStore> {
  static override initClass(): void {
    this.prototype.proxiedField = 'subtemplate';
  }

  templateCollection(): TemplateCollectionSummary {
    return this.proxied().templateCollection;
  }

  latestVersionId(): number {
    return this.proxied().latestVersionId;
  }

  isStale(): boolean | undefined {
    return this.proxied().isStale;
  }

  getStore(): typeof SubtemplateStore {
    return SubtemplateStore;
  }

  getState(): ItemEditorState<Subtemplate, NotePart, 'subtemplate', 'subtemplateId'> {
    return this.getStore().getEditorState(this.id)!;
  }

  openLatestVersion = (): void => {
    SubtemplateActions.open(this.id);
  };

  review(): Review | null | undefined {
    return this.proxied().review;
  }

  override allowEdits(): boolean {
    return canEditTemplate({
      template: this.proxied(),
      isLatest: this.isLatest(),
      dirty: this.isDirty(),
    });
  }

  override setBanner(): void {
    const reviewStatus = this.review()?.status;
    const canViewLatestVersion = this.proxied().canViewLatestVersion ?? this.writable();

    if ((!this.isLatest() || this.isDirty()) && !canViewLatestVersion) {
      this.setBannerText(null);
    } else if (reviewStatus != null && ReviewConstants.LOCKED_STATUSES.includes(reviewStatus)) {
      this.setBannerText(
        <DocumentBanner.ReviewLocked
          status={reviewStatus}
          docType={ModelTypeConstants.SUBTEMPLATE}
          isLatest={this.isLatest()}
          onNavigateToLatest={this.openLatestVersion}
        />
      );
    } else if (BenchlingConfig.ELN_REALTIME_TEMPLATE_UPDATES && this.lockedFromExternalChange()) {
      this.setBannerText(<DocumentBanner.ExternalChangeLocked isUnsaved={this.isUnsaved()} />);
    } else if (!this.isLatest() || this.isDirty()) {
      this.setBannerText(
        <DocumentBanner.PreviousVersion
          canEditLatestVersion={this.writable()}
          docType={ModelTypeConstants.SUBTEMPLATE}
          isEvaluationPersisted={Boolean(
            this.proxied()?.subtemplateVersion?.noteVersion?.noteRuntimeSnapshot?.isEvaluationPersisted
          )}
          onNavigateToLatest={this.openLatestVersion}
        />
      );
    } else if (this.isArchived()) {
      this.setBannerText(<DocumentBanner.Archived docType={ModelTypeConstants.SUBTEMPLATE} />);
    } else if (!this.writable()) {
      this.setBannerText(<DocumentBanner.ReadOnly docType={ModelTypeConstants.SUBTEMPLATE} />);
    } else {
      this.setBannerText(null);
    }
  }

  override setArchiveRecord(archiveRecord: ArchiveRecord): void {
    // Do not bump version and trigger an autosave because archiveRecord is not a versioned field  and has
    // already been posted to the server. See `updateItem` in VersioneditemSubstore
    SubtemplateActions.update(this.id, {$merge: {archiveRecord}}, {cidIdMap: null, bumpVersion: false});
  }

  override setPermissions(permissions: Permissions): void {
    // Do not bump version and trigger an autosave because archiveRecord is not a versioned field  and has
    // already been posted to the server. See `updateItem` in VersioneditemSubstore
    SubtemplateActions.update(this.id, {$merge: {permissions}}, {cidIdMap: null, bumpVersion: false});
  }

  load({versionId, deferred}: {versionId?: number | null; deferred: JQueryDeferred<void>}): void {
    if (this.proxied() != null && versionId != null) {
      // If it's already loaded and they are trying to load a version
      SubtemplateActions.loadVersion(this.id, versionId, {deferred});
    } else {
      // Otherwise, trigger a fresh open.
      SubtemplateActions.open(this.id, {versionId, deferred});
    }
  }

  override cleanUp(): void {
    super.cleanUp();
    SubtemplateActions.close(this.id);
  }

  override async triggerUpdateNameAction(name: string): Promise<string> {
    if (name !== this.name()) {
      await SubtemplateActions.saveFileData(this.id, {data: {name}});
    }
    return this.name();
  }
}
SubtemplateFile.initClass();
export default SubtemplateFile;
